/* Pipeline 172X: icon system
   Clean SVG icons at 16×16. Consistent stroke-width 1.5, rounded caps/joins.
   Named semantically for the product domain, not generic shapes. */

interface IconProps {
  className?: string;
  size?: number;
  "aria-hidden"?: boolean;
}

function Icon({
  d,
  viewBox = "0 0 16 16",
  size = 16,
  className = "",
  strokeWidth = 1.5,
  fill = "none",
}: {
  d: string;
  viewBox?: string;
  size?: number;
  className?: string;
  strokeWidth?: number;
  fill?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill={fill}
      aria-hidden="true"
      className={className}
    >
      <path
        d={d}
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const IconToday = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 1.5A6.5 6.5 0 1 0 8 14.5A6.5 6.5 0 0 0 8 1.5ZM8 4.5v3.5l2.5 1.5"
  />
);

export const IconJobs = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M2 5.5h12M2 5.5a1.5 1.5 0 0 1 1.5-1.5h9A1.5 1.5 0 0 1 14 5.5v7A1.5 1.5 0 0 1 12.5 14h-9A1.5 1.5 0 0 1 2 12.5V5.5ZM5.5 4V3a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 .5.5v1M5.5 9h5M5.5 11.5h3"
  />
);

export const IconCampaigns = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M1.5 12.5l3.5-4L8.5 11l3-5L15 3.5M1.5 12.5h13"
  />
);

export const IconApplications = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M10.5 2H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5L10.5 2ZM10 2v2.5h2.5M5.5 7.5h5M5.5 10h5M5.5 5h2.5"
  />
);

export const IconAnswers = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8c0 1.04.25 2.02.69 2.89L1.5 14.5l3.61-.69A6.5 6.5 0 1 0 8 1.5ZM8 7v.5M8 9.5v.5"
  />
);

export const IconAnalytics = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M2 12.5h12M4 12.5V9M7 12.5V5M10 12.5V7M13 12.5V3.5"
  />
);

export const IconAttention = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 2L1.5 13.5h13L8 2ZM8 7v3M8 11.5v.5"
  />
);

export const IconProfile = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6ZM2.5 14a5.5 5.5 0 0 1 11 0"
  />
);

export const IconSettings = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M6.5 2.5h3M8 2.5v1M6.5 13.5h3M8 12.5v1M2.5 6.5v3M3.5 8h-1M13.5 6.5v3M12.5 8h1M4.22 4.22l1.06 1.06M10.72 10.72l1.06 1.06M4.22 11.78l1.06-1.06M10.72 5.28l1.06-1.06M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z"
  />
);

export const IconSubscription = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M1.5 4.5h13v9h-13V4.5ZM1.5 7.5h13M5 10.5h3"
  />
);

export const IconPrivacy = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 1.5L2 4v4.5C2 11.73 4.7 14.5 8 14.5s6-2.77 6-6V4L8 1.5ZM5.5 8.5L7 10l3.5-3.5"
  />
);

export const IconNotifications = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 1.5a4.5 4.5 0 0 0-4.5 4.5v3L2 11h12l-1.5-2V6A4.5 4.5 0 0 0 8 1.5ZM6.5 11v.5a1.5 1.5 0 0 0 3 0V11"
  />
);

export const IconSecurity = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 1.5L2 4v4.5C2 11.73 4.7 14.5 8 14.5s6-2.77 6-6V4L8 1.5ZM8 5v4M8 10.5h.01"
  />
);

export const IconSearch = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M7 12A5 5 0 1 0 7 2a5 5 0 0 0 0 10ZM14.5 14.5l-3.5-3.5"
  />
);

export const IconChevronRight = ({ size = 16, className = "" }: IconProps) => (
  <Icon size={size} className={className} d="M6 3.5L10.5 8 6 12.5" />
);

export const IconChevronLeft = ({ size = 16, className = "" }: IconProps) => (
  <Icon size={size} className={className} d="M10 3.5L5.5 8 10 12.5" />
);

export const IconChevronDown = ({ size = 16, className = "" }: IconProps) => (
  <Icon size={size} className={className} d="M3.5 6L8 10.5 12.5 6" />
);

export const IconCheck = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M3 8.5L6.5 12 13 5"
    strokeWidth={2}
  />
);

export const IconX = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M3.5 3.5l9 9M12.5 3.5l-9 9"
    strokeWidth={1.75}
  />
);

export const IconExternalLink = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M9 2.5h4.5V7M13.5 2.5l-7 7M6.5 4H3A1.5 1.5 0 0 0 1.5 5.5v7.5A1.5 1.5 0 0 0 3 14.5h7.5A1.5 1.5 0 0 0 12 13V9.5"
  />
);

export const IconDownload = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 2.5v8M5 7.5l3 3 3-3M2 11.5v1A1.5 1.5 0 0 0 3.5 14h9A1.5 1.5 0 0 0 14 12.5v-1"
  />
);

export const IconClock = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 1.5A6.5 6.5 0 1 0 8 14.5A6.5 6.5 0 0 0 8 1.5ZM8 5v3.5L10.5 10"
  />
);

export const IconPause = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M5 3.5v9M11 3.5v9"
    strokeWidth={2}
  />
);

export const IconPlay = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M4.5 3L13.5 8 4.5 13V3Z"
    fill="currentColor"
    strokeWidth={0}
  />
);

export const IconRefresh = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M14 8a6 6 0 0 1-11.5 2.4M2 8a6 6 0 0 1 11.5-2.4M2 5.5V8h2.5M14 8v-2.5h-2.5"
  />
);

export const IconInfo = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 1.5A6.5 6.5 0 1 0 8 14.5A6.5 6.5 0 0 0 8 1.5ZM8 7.5V11M8 5.5v.5"
    strokeWidth={1.75}
  />
);

export const IconWarning = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 2L1.5 13.5h13L8 2ZM8 7v3M8 11.5v.5"
  />
);

export const IconSuccess = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 1.5A6.5 6.5 0 1 0 8 14.5A6.5 6.5 0 0 0 8 1.5ZM5 8.5L7 10.5l4-4"
  />
);

export const IconLock = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M5 7V5.5a3 3 0 0 1 6 0V7M3 7.5h10a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1ZM8 10v2"
  />
);

export const IconDocument = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M10 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5L10 1.5ZM10 1.5v3h2.5M5.5 8h5M5.5 10.5h5M5.5 5.5h2"
  />
);

export const IconEvidence = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M2 4.5h5M2 8h8M2 11.5h4M9.5 10.5l1.5 1.5 3.5-4M13.5 6.5A3.5 3.5 0 1 0 6.5 6.5a3.5 3.5 0 0 0 7 0Z"
  />
);

export const IconKillSwitch = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 2.5v5M5.3 4.3A5 5 0 1 0 10.7 4.3"
    strokeWidth={2}
  />
);

export const IconMenu = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11"
  />
);

export const IconClose = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M3.5 3.5l9 9M12.5 3.5l-9 9"
    strokeWidth={1.75}
  />
);

export const IconFilter = ({ size = 16, className = "" }: IconProps) => (
  <Icon size={size} className={className} d="M2 4h12M4.5 8h7M7 12h2" />
);

export const IconArrowRight = ({ size = 16, className = "" }: IconProps) => (
  <Icon size={size} className={className} d="M2.5 8h11M10 4.5L13.5 8 10 11.5" />
);

export const IconStar = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 1.5l1.8 3.6 4 .58-2.9 2.83.68 3.99L8 10.3l-3.58 1.88.68-3.99L2.2 5.68l4-.58L8 1.5Z"
  />
);

export const IconStarFilled = ({ size = 16, className = "" }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M8 1.5l1.8 3.6 4 .58-2.9 2.83.68 3.99L8 10.3l-3.58 1.88.68-3.99L2.2 5.68l4-.58L8 1.5Z" />
  </svg>
);

export const IconHelp = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 1.5A6.5 6.5 0 1 0 8 14.5A6.5 6.5 0 0 0 8 1.5ZM6 6.5a2 2 0 1 1 2.83 1.83C8.45 8.61 8 9.05 8 9.5M8 11.5v.5"
  />
);

export const IconPackage = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 1.5L1.5 5v6L8 14.5l6.5-3.5V5L8 1.5ZM8 1.5v13M1.5 5l6.5 3.5L14.5 5"
  />
);

export const IconTimeline = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M4 3.5v9M4 3.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM4 9a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM7.5 5h6M7.5 10.5h4"
  />
);

export const IconCamera = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M1.5 5.5h1.9l1.1-2h7l1.1 2h1.9A.5.5 0 0 1 15 6v7.5a.5.5 0 0 1-.5.5H1.5a.5.5 0 0 1-.5-.5V6a.5.5 0 0 1 .5-.5ZM8 11a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"
  />
);

export const IconUpload = ({ size = 16, className = "" }: IconProps) => (
  <Icon
    size={size}
    className={className}
    d="M8 10.5v-8M5 5L8 2.5 11 5M2 11.5v1A1.5 1.5 0 0 0 3.5 14h9A1.5 1.5 0 0 0 14 12.5v-1"
  />
);
